import React from 'react'

import { BottomImageContent, NoImageContent, RightImageContent, TopImageContent } from '..'
import { SectionProps } from '../../../../core/types/Help'
import { List, SectionTitle, Text } from '../ContentItem'
import { HELP_IMAGES } from '../../../constants/HelpImages'
import { buildDynamicCopy } from '../../../../core/utils/Localization'

const Transactions: React.FC<SectionProps> = ({ copy, topicKey }) => {
  const contentMap = {
    'transactions-overview': (
      <React.Fragment>
        <TopImageContent
          altImage={copy.transactions_widget_overview.widget_overview_alt_image}
          content={
            <React.Fragment>
              <Text content={copy.transactions_widget_overview.widget_overview_description} />
              <List
                items={[
                  copy.transactions_widget_overview.widget_overview_new_transactions,
                  copy.transactions_widget_overview.widget_overview_green_amounts,
                  copy.transactions_widget_overview.widget_overview_transaction_details,
                  copy.transactions_widget_overview.widget_overview_filter_accounts,
                  copy.transactions_widget_overview.widget_overview_search_transactions,
                  buildDynamicCopy(
                    copy.transactions_widget_overview.widget_overview_add_transaction_text,
                    <b>{copy.transactions_widget_overview.widget_overview_add_transaction_bold}</b>,
                  ) as React.ReactNode,
                  buildDynamicCopy(
                    copy.transactions_widget_overview.widget_overview_export_csv_text,
                    <b>{copy.transactions_widget_overview.widget_overview_export_csv_bold}</b>,
                  ) as React.ReactNode,
                ]}
              />
            </React.Fragment>
          }
          image={HELP_IMAGES.TRANSACTIONS_OVERVIEW}
          imageLargeBreakPoint={8}
          sx={{ pt: 24 }}
        />
        <SectionTitle
          content={copy.transactions_widget_overview.edit_transactions_title}
          sx={{ pt: 48 }}
        />
        <RightImageContent
          altImage={copy.transactions_widget_overview.edit_transactions_alt_image}
          content={
            <React.Fragment>
              <Text
                content={copy.transactions_widget_overview.edit_transactions_description}
                sx={{ pt: 24 }}
              />
              <List
                items={[
                  copy.transactions_widget_overview.edit_transactions_payee_description,
                  copy.transactions_widget_overview.edit_transactions_date,
                  copy.transactions_widget_overview.edit_transactions_tags,
                  copy.transactions_widget_overview.edit_transactions_memos,
                ]}
              />
              <Text
                content={copy.transactions_widget_overview.edit_transactions_extra_instructions}
                sx={{ pt: 12 }}
              />
              <List
                items={[
                  copy.transactions_widget_overview.edit_transactions_flag_tracking,
                  copy.transactions_widget_overview.edit_transactions_split_transaction,
                  copy.transactions_widget_overview.edit_transactions_exclude_from_reports,
                  copy.transactions_widget_overview.edit_transactions_hide_transaction,
                ]}
              />
            </React.Fragment>
          }
          image={HELP_IMAGES.TRANSACTIONS_EDIT}
        />
        <SectionTitle
          content={copy.transactions_widget_overview.categorizing_transactions_title}
          sx={{ pt: 48 }}
        />
        <BottomImageContent
          altImage={copy.transactions_widget_overview.categorizing_transactions_alt_image}
          content={
            <React.Fragment>
              <Text
                content={copy.transactions_widget_overview.categorizing_transactions_description}
              />
              <Text
                content={
                  copy.transactions_widget_overview.categorizing_transactions_change_category
                }
                sx={{ pt: 12 }}
              />
              <List
                items={[
                  buildDynamicCopy(
                    copy.transactions_widget_overview.categorizing_transactions_select_category,
                    <b>
                      {copy.transactions_widget_overview.categorizing_transactions_category_bold}
                    </b>,
                  ) as React.ReactNode,
                  copy.transactions_widget_overview.categorizing_transactions_select_subcategory,
                ]}
                listType="ol"
              />
            </React.Fragment>
          }
          image={HELP_IMAGES.TRANSACTIONS_CATEGORIZE}
          imageLargeBreakPoint={10}
        />
        <SectionTitle
          content={copy.transactions_widget_overview.transaction_rules_title}
          sx={{ pt: 48 }}
        />
        <BottomImageContent
          altImage={copy.transactions_widget_overview.transaction_rules_alt_image}
          content={
            <React.Fragment>
              <Text content={copy.transactions_widget_overview.transaction_rules_description} />
              <Text
                content={copy.transactions_widget_overview.transaction_rules_create_rule}
                sx={{ pt: 12 }}
              />
              <List
                items={[
                  copy.transactions_widget_overview.transaction_rules_recategorize,
                  buildDynamicCopy(
                    copy.transactions_widget_overview.transaction_rules_apply_to_all_text,
                    <b>{copy.transactions_widget_overview.transaction_rules_apply_to_all_bold}</b>,
                  ) as React.ReactNode,
                ]}
                listType="ol"
              />
              <Text
                content={copy.transactions_widget_overview.transaction_rules_edit_rule}
                sx={{ pt: 12 }}
              />
              <List
                items={[
                  buildDynamicCopy(
                    copy.transactions_widget_overview.transaction_rules_go_to_settings,
                    <b>
                      {copy.transactions_widget_overview.transaction_rules_go_to_settings_bold}
                    </b>,
                  ) as React.ReactNode,
                  copy.transactions_widget_overview.transaction_rules_edit_payee_category,
                  buildDynamicCopy(
                    copy.transactions_widget_overview.transaction_rules_save_text,
                    <b>{copy.transactions_widget_overview.transaction_rules_save_bold}</b>,
                  ) as React.ReactNode,
                ]}
                listType="ol"
              />
              <Text
                content={copy.transactions_widget_overview.transaction_rules_delete_rule}
                sx={{ pt: 12 }}
              />
              <List
                items={[
                  buildDynamicCopy(
                    copy.transactions_widget_overview.transaction_rules_delete_go_to_settings,
                    <b>
                      {
                        copy.transactions_widget_overview
                          .transaction_rules_delete_go_to_settings_bold
                      }
                    </b>,
                  ) as React.ReactNode,
                  copy.transactions_widget_overview.transaction_rules_select_rule_delete_icon,
                  buildDynamicCopy(
                    copy.transactions_widget_overview.transaction_rules_delete_text,
                    <b>{copy.transactions_widget_overview.transaction_rules_delete_bold}</b>,
                  ) as React.ReactNode,
                ]}
                listType="ol"
              />
            </React.Fragment>
          }
          image={HELP_IMAGES.TRANSACTIONS_RULES}
          imageLargeBreakPoint={10}
        />
        <NoImageContent
          content={
            <React.Fragment>
              <SectionTitle content={copy.transactions_widget_overview.split_transactions_title} />
              <Text content={copy.transactions_widget_overview.split_transactions_description} />
              <Text
                content={copy.transactions_widget_overview.split_transactions_instructions_title}
                sx={{ pt: 12 }}
              />
              <List
                items={[
                  copy.transactions_widget_overview.split_transactions_select_transaction,
                  buildDynamicCopy(
                    copy.transactions_widget_overview.split_transactions_select_split_transaction,
                    <b>
                      {
                        copy.transactions_widget_overview
                          .split_transactions_select_split_transaction_bold
                      }
                    </b>,
                  ) as React.ReactNode,
                  copy.transactions_widget_overview.split_transactions_add_splits,
                  buildDynamicCopy(
                    copy.transactions_widget_overview.split_transactions_save_text,
                    <b>{copy.transactions_widget_overview.split_transactions_save_bold}</b>,
                  ) as React.ReactNode,
                ]}
                listType="ol"
              />
              <Text
                content={copy.transactions_widget_overview.split_transactions_delete_split}
                sx={{ pt: 12 }}
              />
              <List
                items={[
                  copy.transactions_widget_overview
                    .split_transactions_delete_select_split_transaction,
                  buildDynamicCopy(
                    copy.transactions_widget_overview.split_transactions_delete_split_text,
                    <b>{copy.transactions_widget_overview.split_transactions_delete_split_bold}</b>,
                  ) as React.ReactNode,
                  buildDynamicCopy(
                    copy.transactions_widget_overview.split_transactions_delete_text,
                    <b>{copy.transactions_widget_overview.split_transactions_delete_bold}</b>,
                  ) as React.ReactNode,
                ]}
                listType="ol"
              />
            </React.Fragment>
          }
          hasDivider={false}
          sx={{ pt: 48 }}
        />
      </React.Fragment>
    ),
    'seeing-duplicate-transactions': (
      <React.Fragment>
        <Text content={copy.duplicate_transactions.duplicate_transactions_title} sx={{ pt: 24 }} />
        <List
          items={[
            buildDynamicCopy(
              copy.duplicate_transactions.duplicate_accounts,
              <b>{copy.duplicate_transactions.duplicate_accounts_bold}</b>,
              <b>{copy.duplicate_transactions.account_details_mark_as_duplicate_bold}</b>,
            ) as React.ReactNode,
            buildDynamicCopy(
              copy.duplicate_transactions.both_debit_credit_sides,
              <b>{copy.duplicate_transactions.both_debit_credit_sides_bold}</b>,
            ) as React.ReactNode,
            buildDynamicCopy(
              copy.duplicate_transactions.data_issues_institution,
              <b>{copy.duplicate_transactions.data_issues_institution_bold}</b>,
            ) as React.ReactNode,
          ]}
        />
      </React.Fragment>
    ),
    'missing-transactions': (
      <React.Fragment>
        <Text content={copy.missing_transactions.missing_transactions_title} sx={{ pt: 24 }} />
        <List
          items={[
            buildDynamicCopy(
              copy.missing_transactions.processing_time,
              <b>{copy.missing_transactions.processing_time_bold}</b>,
            ) as React.ReactNode,
            buildDynamicCopy(
              copy.missing_transactions.new_accounts,
              <b>{copy.missing_transactions.new_accounts_bold}</b>,
            ) as React.ReactNode,
            buildDynamicCopy(
              copy.missing_transactions.infrequent_logins,
              <b>{copy.missing_transactions.infrequent_logins_bold}</b>,
            ) as React.ReactNode,
          ]}
        />
        <Text content={copy.missing_transactions.missing_transactions_footer} sx={{ pt: 12 }} />
      </React.Fragment>
    ),
    'pending-transactions': (
      <React.Fragment>
        <Text content={copy.pending_transactions.pending_transactions_title} sx={{ pt: 24 }} />
        <List
          items={[
            buildDynamicCopy(
              copy.pending_transactions.pending_transactions_details,
              <b>date, description, or amount</b>,
            ) as React.ReactNode,
            copy.pending_transactions.pending_transactions_edit_warning,
          ]}
        />
      </React.Fragment>
    ),
    'exporting-transactions': (
      <TopImageContent
        altImage={copy.export_transactions.export_transactions_alt_image}
        content={
          <React.Fragment>
            <Text
              content={
                buildDynamicCopy(
                  copy.export_transactions.export_transactions_title,
                  <b>CSV file</b>,
                ) as React.ReactNode
              }
            />
            <Text content={copy.export_transactions.export_to_export} sx={{ pt: 12 }} />
            <List
              items={[
                buildDynamicCopy(
                  copy.export_transactions.export_filter_transactions,
                  <b>Filter accounts</b>,
                ) as React.ReactNode,
                buildDynamicCopy(
                  copy.export_transactions.export_csv,
                  <b>Export CSV</b>,
                ) as React.ReactNode,
                copy.export_transactions.export_save_file,
              ]}
              listType="ol"
            />
          </React.Fragment>
        }
        hasDivider={false}
        image={HELP_IMAGES.TRANSACTIONS_EXPORT}
        imageLargeBreakPoint={10}
        sx={{ pt: 24 }}
      />
    ),
  }
  return contentMap[topicKey as keyof typeof contentMap]
}

export default Transactions
